import type { Dispatch, SetStateAction } from 'react';
import { useContext, createContext } from 'react';
import { type ChatResponseType } from '@trinity/chat';
import { createNamedContext } from '@trinity/chat-components/utils';

type TextToSpeechAudioPlayerRefValue = [HTMLAudioElement | undefined, Dispatch<HTMLAudioElement | undefined>];

type InitiateQuerySpeechToTextContextValue = (data: Omit<TextToSpeechState, 'state'>) => unknown;
export type TextToSpeechAudioMetadata = {
  queryId: number | null;
  loadingState: 'loading' | 'loaded' | 'error' | null;
};
export type TextToSpeechState = {
  queryId: number | null;
  state: 'playing' | 'paused' | 'loading' | 'error' | 'stopped' | null;
};

export type VoiceModeContextValue = {
  dequeue: () => void;
  enabled: boolean;
  enable: () => void;
  disable: () => void;
  enqueue: (data: { queryId: number; type: ChatResponseType }) => void;
  queryId: number | null;
  type: ChatResponseType | null;
  toggle: () => void;
};

export type MicState = 'inactive' | 'waiting-for-permission' | 'listening' | 'error';

export type MicrophoneStateContextValue = [MicState, Dispatch<SetStateAction<MicState>>];

export const TextToSpeechAudioPlayerRefContext = createNamedContext<
  TextToSpeechAudioPlayerRefValue | undefined
>(undefined, 'TextToSpeechAudioPlayer.Context');

export type AudioContextValue = {
  onPlay: (audioUrl: string) => void;
  onStop: () => void;
  src: string | null;
};

type StopTextToSpeechContextValue = (isNavigationAction?: boolean) => void;

export const OnTextToSpeechAudioReadyContext = createNamedContext<((audioUrl: string | null) => void) | null>(
  null,
  'OnTextToSpeechAudioReady'
);
export const InitiateQuerySpeechToText = createNamedContext<InitiateQuerySpeechToTextContextValue | null>(
  null,
  'InitiateQuerySpeechToText'
);
export const TextToSpeechStateContext = createNamedContext<TextToSpeechState | null>(
  null,
  'TextToSpeechState'
);
export const StopTextToSpeechContext = createNamedContext<StopTextToSpeechContextValue | undefined>(
  undefined,
  'StopTextToSpeech'
);
export const VoiceModeContext = createNamedContext<VoiceModeContextValue | undefined>(
  undefined,
  'VoiceMode.Context'
);
export const MicrophoneStateContext = createNamedContext<MicrophoneStateContextValue | undefined>(
  undefined,
  'MicrophoneState.Context'
);
export const useTextToSpeechAudioPlayerRef = () => useContext(TextToSpeechAudioPlayerRefContext);
export const LoadingVoiceClipContext = createContext<AudioContextValue | null>(null);

export const useInitiateQuerySpeechToTextNullSafe = (): InitiateQuerySpeechToTextContextValue => {
  const initiateQuerySpeechToText = useContext(InitiateQuerySpeechToText);
  if (!initiateQuerySpeechToText) {
    throw new Error('InitiateQuerySpeechToText provider is missing');
  }
  return initiateQuerySpeechToText;
};

export const useStopTextToSpeechContextNullSafe = (): StopTextToSpeechContextValue => {
  const stopTextToSpeechContext = useContext(StopTextToSpeechContext);
  if (!stopTextToSpeechContext) {
    throw new Error('StopTextToSpeechContext provider is missing');
  }
  return stopTextToSpeechContext;
};

export const useTextToSpeechStateContextNullSafe = (): TextToSpeechState => {
  const textToSpeechState = useContext(TextToSpeechStateContext);
  if (!textToSpeechState) {
    throw new Error('TextToSpeechStateContext provider is missing');
  }
  return textToSpeechState;
};

export const useMicrophoneStateContextNullSafe = () => {
  const microphoneStateContext = useContext(MicrophoneStateContext);
  if (!microphoneStateContext) {
    throw new Error('MicrophoneStateContext provider is missing');
  }
  return microphoneStateContext;
};

export const useLoadingVoiceClipContextNullSafe = () => {
  const loadingVoiceClipContext = useContext(LoadingVoiceClipContext);
  if (!loadingVoiceClipContext) {
    throw new Error('LoadingVoiceClipContext provider is missing');
  }
  return loadingVoiceClipContext;
};
